import { Button, Input, Modal, Space, message, Divider } from 'antd';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { post } from '../../../../../axios/axios';
import { useDevice } from '../../../../../hooks/useDevice';
import { summaryStore } from '../../../store';
import { getSelectedPackageEntitlement } from '../../../../../Service/packageEntitlements';

const SelectCoupon = NiceModal.create(({ initialCode = '' }) => {
    const modal = useModal();
    const [couponCode, setCouponCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [couponDetail, setCouponDetail] = useState(null);
    const [exclusiveCode, setExclusiveCode] = useState('');
    const [exclusiveCodeLoading, setExclusiveCodeLoading] = useState(false);
    const requestRef = useRef(0);

    // 查询优惠券详情
    const handleExchange = useCallback(async (value) => {
        const normalized = value.trim();
        if (!normalized) {
            message.error('请输入优惠券码');
            return;
        }

        const request = ++requestRef.current;
        setLoading(true);
        setCouponDetail(null);
        try {
            const res = await post('/member/coupon/code/detail', {
                code: normalized,
            });

            if (request !== requestRef.current) return;
            if (res.success && res.data) {
                setCouponCode(normalized);
                setCouponDetail({ ...res.data, code: normalized });
                message.success('优惠券可用');
            } else {
                message.error(res.msg || '优惠券不可用');
            }
        } catch (error) {
            console.error(error);
            if (request === requestRef.current) message.error('查询失败，请重试');
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (modal.visible) {
            const selected = getSelectedPackageEntitlement();
            const code =
                initialCode || (Number(selected?.codeType) === 1 ? selected.redeemCode : '');
            setCouponCode(code);
            setCouponDetail(null);
            setLoading(false);
            if (code) handleExchange(code);
        }
        return () => {
            requestRef.current++;
        };
    }, [modal.visible, initialCode, handleExchange]);

    // 使用优惠券
    const handleUseCoupon = () => {
        if (!couponDetail || couponDetail.code !== couponCode.trim()) return;

        modal.resolve({
            ...couponDetail,
            code: couponDetail.code,
        });
        modal.hide();
    };

    // 绑定专属口令
    const handleBindExclusiveCode = async () => {
        if (!exclusiveCode.trim()) {
            message.error('请输入专属口令');
            return;
        }

        // 检查 paperInfo.id 是否存在
        if (!summaryStore.paperInfo?.id) {
            message.error('论文信息不完整，无法绑定专属口令');
            return;
        }

        setExclusiveCodeLoading(true);
        try {
            const res = await post('/member/commission/bind-code', {
                paperId: summaryStore.paperInfo.id,
                exclusiveCode: exclusiveCode.trim(),
            });

            if (res.code === 0 || res.success) {
                message.success('专属口令绑定成功');
                modal.resolve({
                    type: 'exclusiveCode',
                    code: exclusiveCode.trim(),
                });
                modal.hide();
            }
        } catch (error) {
            console.error('绑定专属口令失败:', error);
        } finally {
            setExclusiveCodeLoading(false);
        }
    };

    // 取消选择
    const handleCancel = () => {
        modal.reject(new Error('用户取消选择'));
        modal.hide();
    };
    const { isMobile } = useDevice();
    return (
        <Modal
            title="优惠券"
            open={modal.visible}
            onCancel={handleCancel}
            footer={null}
            width={isMobile ? '100%' : 600}
            maskClosable={false}
            destroyOnClose
            className="rounded-lg"
            zIndex={1889}
            styles={{
                header: {
                    background: 'transparent',
                },
                content: {
                    background: 'linear-gradient(90deg, #edfff7 0%, #ffffff 100%)',
                },
            }}
        >
            <div className="flex flex-col gap-4">
                {/* 优惠券码输入区域 */}
                <Space.Compact>
                    <Input
                        placeholder="请输入优惠券码"
                        value={couponCode}
                        onChange={(e) => {
                            requestRef.current++;
                            setCouponCode(e.target.value);
                            setCouponDetail(null);
                            setLoading(false);
                        }}
                        className="flex-1"
                    />
                    <Button
                        type="primary"
                        onClick={() => handleExchange(couponCode)}
                        loading={loading}
                    >
                        查看优惠券
                    </Button>
                </Space.Compact>

                {/* 优惠券展示区域 */}
                {couponDetail && (
                    <div className="mt-4 rounded-lg bg-white p-6">
                        <div className="flex items-start">
                            {/* 左侧价格区域 */}
                            <div className="flex w-[100px] flex-col items-start border-r border-[#EEEEF1] pr-3">
                                <div className="flex items-baseline">
                                    <span className="text-sm text-[#FF4B4B]">¥</span>
                                    <span className="text-[40px] font-bold leading-none text-[#FF4B4B]">
                                        {couponDetail.discount}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs text-[#FF4B4B]">
                                    满{couponDetail.price}可用
                                </div>
                            </div>

                            {/* 右侧内容区域 */}
                            <div className="flex flex-1 flex-col justify-center pl-3">
                                <div className="text-base font-medium text-[#192038]">
                                    {couponDetail.title}
                                </div>
                                <div className="mt-1 text-xs text-[#8C93B1]">
                                    {couponDetail.description}
                                </div>
                            </div>
                        </div>
                        <Button type="primary" block onClick={handleUseCoupon} className="mt-6">
                            使用优惠券
                        </Button>
                    </div>
                )}

                {/* 专属口令输入区域 */}
                {summaryStore.paperInfo?.id && (
                    <>
                        <Divider className="my-4" />
                        <div className="flex flex-col gap-2">
                            <div className="text-sm font-medium text-[#192038]">专属口令</div>
                            <Space.Compact>
                                <Input
                                    placeholder="请输入专属口令"
                                    value={exclusiveCode}
                                    onChange={(e) => setExclusiveCode(e.target.value)}
                                    className="flex-1"
                                    onPressEnter={handleBindExclusiveCode}
                                />
                                <Button
                                    type="primary"
                                    onClick={handleBindExclusiveCode}
                                    loading={exclusiveCodeLoading}
                                >
                                    使用
                                </Button>
                            </Space.Compact>
                            <div className="text-xs text-[#8C93B1]">
                                输入专属口令后点击使用，每个论文只能绑定一次
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
});

export default SelectCoupon;
